import { memo, type FC, type ReactNode } from "react";
import { Icon, type IconNameType } from "@src/components/Icon";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import type { StyleProp, ViewStyle } from "react-native";
import { View } from "@src/components/View";
import { Text } from "@src/components/Text";
import { TextSize } from "@src/components/Text";

export type BannerVariant = "warning" | "info";

type BannerProps = {
  title?: string;
  message?: string;
  variant?: BannerVariant;
  textTitleStyle?: TextSize;
  textMessageStyle?: TextSize;
  containerStyle?: StyleProp<ViewStyle>;
  iconStyle?: StyleProp<ViewStyle>;
  iconColor?: string;
};

export const Banner: FC<BannerProps> = memo(
  ({
    title,
    message,
    variant = "warning",
    textTitleStyle = "xs/semibold",
    textMessageStyle = "xxs/regular",
    containerStyle,
    iconStyle,
    iconColor,
  }) => {
    const { theme } = useUnistyles();
    const accentColor =
      variant === "info" ? theme.colors.interactive.buttons.secondary_foreground : theme.colors.status.warning;
    const textColor = variant === "info" ? theme.colors.text : theme.colors.textCardInnerWarning;
    const palette =
      variant === "info" ?
        {
          background: theme.colors.surfaceCardInner,
          border: accentColor,
          icon: accentColor,
          iconName: "infoOutline" as IconNameType,
        }
      : {
          background: theme.colors.surfaceCardInnerWarning,
          border: accentColor,
          icon: accentColor,
          iconName: "clockOutline" as IconNameType,
        };

    return (
      <View
        style={[
          styles.container,
          {
            backgroundColor: palette.background,
            borderColor: palette.border,
          },
          containerStyle,
        ]}
      >
        <View flexDirection="row" gap="md" alignItems="center">
          <Icon name={palette.iconName} size={20} color={iconColor ?? palette.icon} style={[styles.icon, iconStyle]} />
          <View gap="xs" style={styles.textContainer}>
            {title && (
              <Text textStyle={textTitleStyle} style={{ color: textColor }}>
                {title}
              </Text>
            )}
            {message && (
              <Text textStyle={textMessageStyle} style={{ color: textColor }}>
                {message}
              </Text>
            )}
          </View>
        </View>
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    padding: theme.spacing.lg,
    paddingRight: theme.spacing["3xl"],
    borderRadius: theme.spacing.xs,
    borderWidth: 1,
  },
  icon: {
    marginTop: theme.spacing.xxs,
    alignSelf: "flex-start",
  },
  title: {
    color: theme.colors.text,
  },
  textContainer: {
    flex: 1,
    paddingRight: theme.spacing["3xl"],
  },
}));
